import React from 'react';
import { Box, Typography, Grid, Paper, Button, Chip, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VideocamIcon from '@mui/icons-material/Videocam';
import MemoryIcon from '@mui/icons-material/Memory';
import BugReportIcon from '@mui/icons-material/BugReport';
import HandymanIcon from '@mui/icons-material/Handyman';
import RedeemIcon from '@mui/icons-material/Redeem';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import services from '../data/services';

const icons: Record<string, React.ReactNode> = {
  '3d-modelling': <ViewInArIcon sx={{ fontSize: 40 }} />,
  'vtubing-setup': <VideocamIcon sx={{ fontSize: 40 }} />,
  'pcb-design': <MemoryIcon sx={{ fontSize: 40 }} />,
  'security-research': <BugReportIcon sx={{ fontSize: 40 }} />,
  'vtuber-support': <HandymanIcon sx={{ fontSize: 40 }} />,
  'merch-design': <RedeemIcon sx={{ fontSize: 40 }} />,
};

export default function Services() {
  return (
    <Box
      id="services"
      sx={{
        py: { xs: 10, md: 16 },
        px: { xs: 3, md: 8 },
        background: 'linear-gradient(180deg, transparent, rgba(180,79,255,0.04), transparent)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem' }}>
          02 — SERVICES
        </Typography>
        <Typography variant="h2" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 2, mt: 1 }}>
          What I Offer
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 8, maxWidth: 540 }}>
          From digital avatars to physical circuits — I create things in and out of the cyberspace and the real world.
        </Typography>

        <Grid container spacing={4}>
          {services.map((svc) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={svc.id}>
              <Paper
                sx={{
                  p: 3.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(22,15,32,0.9)',
                  border: '1px solid rgba(255,110,180,0.12)',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: svc.color,
                    boxShadow: `0 0 30px ${svc.color}22`,
                    transform: 'translateY(-6px)',
                  },
                }}
              >
                <Box sx={{ color: svc.color, mb: 2 }}>{icons[svc.id]}</Box>

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {svc.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {svc.subtitle}
                </Typography>

                <Typography variant="h6" sx={{ color: svc.color, fontWeight: 700, mb: 2, fontSize: '1rem' }}>
                  {svc.price}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3, flex: 1 }}>
                  {svc.shortDesc}
                </Typography>

                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8, mb: 3 }}>
                  {svc.tags.slice(0, 4).map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        background: `${svc.color}18`,
                        border: `1px solid ${svc.color}44`,
                        color: svc.color,
                      }}
                    />
                  ))}
                </Stack>

                <Button
                  component={RouterLink}
                  to={`/services/${svc.id}`}
                  fullWidth
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    borderColor: `${svc.color}66`,
                    color: svc.color,
                    '&:hover': {
                      borderColor: svc.color,
                      background: `${svc.color}12`,
                    },
                  }}
                >
                  View Details
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
